<template>
  <section class="gallery-widget">
    <Caption v-viewport-animated :content="widget.title" />

    <div class="content grid">
      <div
        v-viewport-animated
        class="description"
        v-html="nbsp(widget.description)"
      />

      <Swiper
        :navigation="{
          prevEl: `#widget-${widget.id} .navigation-button--prev`,
          nextEl: `#widget-${widget.id} .navigation-button--next`
        }"
        :pagination="widget.images.length > 1 ? { type: 'fraction' } : false"
        :slides-per-view="1"
        class="slider"
        @slide-change="onSlideChange"
      >
        <template v-if="widget.images.length > 1" #navigation>
          <div class="buttons">
            <button class="navigation-button navigation-button--prev">
              <ArrowLeftIcon class="arrow-icon" />
            </button>
            <button class="navigation-button navigation-button--next">
              <ArrowRightIcon class="arrow-icon" />
            </button>
          </div>
        </template>

        <SwiperSlide v-for="(image, index) in widget.images" :key="index">
          <BaseImage
            class="slide-image"
            :image="image.image"
            v-bind="
              getImageSizes({
                xs: { w: 360, h: 540 },
                md: { w: 532, h: 406 },
                lg: { w: 1000, h: 667 }
              })
            "
            @click="openLightbox(index)"
          />
        </SwiperSlide>
      </Swiper>

      <div class="side-panel">
        <IconEnum class="chocolate-icon" :type="'chocolate'" />

        <Swiper
          ref="sideSwiperComponent"
          :slides-per-view="1"
          :initial-slide="1"
          class="side-image"
        >
          <SwiperSlide v-for="(image, index) in widget.images" :key="index">
            <BaseImage
              class="slide-image"
              :image="image.image"
              v-bind="
                getImageSizes({
                  md: { w: 236, h: 173 },
                  lg: { w: 366, h: 243 }
                })
              "
              @click="openLightbox(index)"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>

    <BasePopup :active="lightboxVisible" @close="closeLightbox">
      <Lightbox :images="widget.images" :current-slide="activeSlide" />
    </BasePopup>
  </section>
</template>

<script lang="ts" setup>
import type { Swiper as SwiperClass } from 'swiper/types'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?component'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?component'
import Swiper from '@/components/Swiper/index.vue'
import type { PublicGalleryWidget } from '@/service/__generated-api'
import type { Widget } from '@/types/utils'

defineProps<Widget<PublicGalleryWidget>>()

const sideSwiperComponent = ref<InstanceType<typeof Swiper>>()

const lightboxVisible = ref(false)
const activeSlide = ref(0)

const closeLightbox = () => {
  lightboxVisible.value = false
}

const openLightbox = (index: number) => {
  activeSlide.value = index
  lightboxVisible.value = true
}

const onSlideChange = (swiper: SwiperClass) => {
  if (!sideSwiperComponent.value) return

  activeSlide.value = swiper.realIndex
  sideSwiperComponent.value.swiper?.slideTo(swiper.realIndex + 1)
}
</script>

<style lang="scss" scoped>
.content {
  margin-top: rem(36px);

  @include media-up(md) {
    margin-top: rem(34px);
  }

  @include media-up(lg) {
    margin-top: rem(72px);
  }
}

.description {
  @include wysiwyg;
  @include media-column(
    (
      xs: 24,
      md: 14 1,
      lg: 12 1
    )
  );
  margin-bottom: rem(25px);
  color: $dark-brown;

  @include media-up(md) {
    margin-bottom: rem(42px);
  }

  @include media-up(lg) {
    margin-bottom: rem(46px);
    margin-left: rem(30px);
  }
}

.slider {
  @include media-column(
    (
      xs: all,
      md: 16 -1,
      lg: 17 -1
    )
  );
  position: relative;
  width: 100%;
  aspect-ratio: 2/3;

  @include media-up(md) {
    aspect-ratio: 4/3;
  }

  @include media-up(lg) {
    aspect-ratio: 3/2;
  }

  :deep(.swiper-pagination) {
    @include t-caption5;
    width: max-content;
    padding: rem(14px) rem(34px);
    background-color: $light-blue-2;

    @include media-up(md) {
      padding: rem(14px) rem(37px);
    }

    @include media-up(lg) {
      padding: rem(19px) rem(47px);
    }
  }

  :deep(.controls) {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;

    color: $dark-brown;
  }
}

.slide-image {
  cursor: pointer;
  transition: transform $transition-slow;

  &:hover {
    transform: scale(1.05);
  }
}

.buttons {
  display: flex;
  gap: rem(19px);
  padding: rem(20px) rem(30px);
  background: $beige;

  @include media-up(md) {
    padding: rem(20px) rem(26px);
  }

  @include media-up(lg) {
    padding: rem(21px) rem(33px);
  }
}

.navigation-button {
  @include size(rem(50px));
  padding: rem(13px);
  color: $beige;
  background: $dark-brown;
  border-radius: 50%;
  box-shadow: $shadow-type-1;
  transition:
    box-shadow $transition,
    background-color $transition;

  @include media-up(lg) {
    @include size(rem(64px));
    padding: rem(20px);
  }

  &:disabled {
    background: $beige-gray;
    box-shadow: none;
  }
}

.arrow-icon {
  @include size(100%);
}

.side-panel {
  @include hide-down(md);
  @include media-column(
    (
      md: 10 15,
      lg: 9 16
    )
  );
  margin-top: auto;
}

.chocolate-icon {
  @include size(rem(48px));
  display: block;

  @include media-up(lg) {
    @include size(rem(85px));
  }
}

.side-image {
  height: auto;
  aspect-ratio: 4/3;

  @include media-up(lg) {
    aspect-ratio: 3/2;
  }
}
</style>
